import React, { useContext, useEffect } from 'react';
import { PLAYER_STATUS } from 'constants/gameConstants';
import GameContext from 'contexts/GameContext';
import { useImBackRequest } from 'hooks/useImBack';
import { useTableSelector } from 'hooks/useTableSelector';

import styles from './SitOutOverlay.module.scss';

// Big centered "I'm Back" poker-chip button shown over the felt while the seated
// player is paused — whether they sat out manually, were auto-sat-out after
// missing actions, or stood up to observe. Per the design it's a round orange
// chip with a dashed inner ring and radar-style pulse rings emanating from it.
const SitOutOverlay: React.FC = () => {
  const seatId = useTableSelector((table) => table.currentPlayer.seatId);
  const isSeated = useTableSelector((table) => table.currentPlayer.isSeated);
  const seatStatus = useTableSelector((table) => (table.game as any)[`p${seatId}status`]);

  const requestImBack = useImBackRequest();
  // Drive off the same optimistic flags the top-right Sit Out / Observe / I'm
  // Back controls use, so the overlay shows/hides instantly with those controls
  // instead of waiting for the server seat status to round-trip.
  const { isSatOut, setIsSatOut, isStandingUp, setIsStandingUp, pendingImBack } =
    useContext(GameContext);

  // Mirror the engine seat status into those optimistic flags so the overlay
  // reflects EVERY way a player ends up paused — not just a manual Sit Out.
  // SitOutButton mirrors the manual sit-out (statuses 0/1), but it isn't always
  // mounted (e.g. multi-table grid), so the overlay can't rely on it. Statuses
  // (PLAYER_STATUS): SITTING_OUT(1) manual sit out · FOR_SITOUT(6) auto sit out
  // after missing actions · LEAVING(2) stood up to observe. ANY other status —
  // ACTIVE(0) playing, POST_BLIND(4) / WAIT_FOR_BB(5) joining — means the player
  // is NOT paused, so both flags are cleared. Clearing on the non-paused
  // branch is what fixes the reported bug: a player who observed (isStandingUp
  // set), got removed, then re-joined by tapping a seat comes back as
  // JOINING(4/5) — a status the old code ignored, leaking a stale "I'm Back".
  // Skip an unset/NaN status so a transient broadcast gap doesn't flicker the
  // flags. Only reacts to status CHANGES so a fresh "I'm Back" click (which
  // clears the flags optimistically while the seat is still flagged) isn't
  // immediately re-flipped back.
  useEffect(() => {
    const status = +seatStatus;
    if (!Number.isFinite(status)) return;
    if (status === PLAYER_STATUS.SITTING_OUT || status === PLAYER_STATUS.FOR_SITOUT) {
      setIsSatOut(true);
      setIsStandingUp(false);
    } else if (status === PLAYER_STATUS.LEAVING) {
      setIsStandingUp(true);
      setIsSatOut(false);
    } else {
      setIsSatOut(false);
      setIsStandingUp(false);
    }
  }, [seatStatus, setIsSatOut, setIsStandingUp]);

  // Show while the player still holds a seat and is paused — sat out (manual or
  // missed-actions) OR observing (stood up). Both return to play via imBack.
  //
  // `pendingImBack` hides it the instant the player presses during a dealer
  // button / blind assignment, while useDeferredImBack holds the send until the
  // engine will accept it. Without that the overlay would sit there looking
  // dead, which is the bug this replaced: the old handler gated on
  // `hand === '' || +hand > 3` and silently dropped the click.
  if (!isSeated || pendingImBack || (!isSatOut && !isStandingUp)) return null;

  const handleImBack = () => {
    requestImBack();
  };

  return (
    <div className={styles.wrap}>
      <button className={styles.button} onClick={handleImBack} aria-label="I'm Back">
        <span className={styles.pulse} aria-hidden />
        <span className={`${styles.pulse} ${styles.pulseDelay}`} aria-hidden />
        <span className={styles.core}>
          <span className={styles.label}>I&apos;m Back</span>
        </span>
      </button>
    </div>
  );
};

export default SitOutOverlay;
